import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { getAdminViewMode } from '../../utils/adminViewMode';
import {
  getPreferredBootcampIdForTeam,
  getTeamDetail,
  writeAdminAnalyticsSelection,
  writeManagerSelection,
} from '../../utils/managerAccess';
import {
  getRegionActivityByClient,
  getTopicActivityByClient,
} from '../../pages/ManagerHome';
import { getTeamActivityByRegion } from '../../pages/ClientViewer';
import AdminAnalyticsLanding from '../analytics/AdminAnalyticsLanding';
import AdminClientAnalyticsSummary from '../analytics/AdminClientAnalyticsSummary';
import AdminClientActivityLists from '../analytics/AdminClientActivityLists';
import AdminRegionAnalyticsSummary from '../analytics/AdminRegionAnalyticsSummary';
import AdminRegionActivityLists from '../analytics/AdminRegionActivityLists';

const OVERVIEW_CACHE_TTL_MS = 10 * 60 * 1000;

function readOverviewCache(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if ((Date.now() - (parsed?.savedAt || 0)) >= OVERVIEW_CACHE_TTL_MS) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

function writeOverviewCache(cacheKey, data) {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {}
}

function clientOverviewCacheKey(isAdminAnalytics, uid, clientId) {
  return isAdminAnalytics
    ? `admin.analytics.overview.${uid}.${clientId}.v5`
    : `manager.overview.${uid}.${getAdminViewMode()}.${clientId}.v5`;
}

function regionOverviewCacheKey(isAdminAnalytics, uid, clientId, regionId) {
  return isAdminAnalytics
    ? `admin.analytics.regionOverview.${uid}.${clientId}.${regionId}.v5`
    : `manager.regionOverview.${uid}.${getAdminViewMode()}.${clientId}.${regionId}.v5`;
}

function teamDetailCacheKey(isAdminAnalytics, uid, clientId, regionId, teamId) {
  return isAdminAnalytics
    ? `admin.analytics.teamDetail.${uid}.${clientId}.${regionId}.${teamId}.v5`
    : `manager.teamDetail.${uid}.${getAdminViewMode()}.${clientId}.${regionId}.${teamId}.v5`;
}

function ManagerOverviewPanel({ hierarchy, overviewCacheNamespace = 'manager' }) {
  const navigate = useNavigate();
  const {
    clientId,
    regionId,
    teamId,
    userId,
    selectedRegion,
    hasClientScope,
    setTeam,
    loading: hierarchyLoading,
  } = hierarchy;

  const isAdminAnalytics = overviewCacheNamespace === 'admin-analytics';
  const writeSelection = isAdminAnalytics ? writeAdminAnalyticsSelection : writeManagerSelection;
  const analyticsBasePath = isAdminAnalytics ? '/admin/analytics' : '/manager';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsByClient, setAnalyticsByClient] = useState({});
  const [topicByClient, setTopicByClient] = useState({});
  const [regionAnalytics, setRegionAnalytics] = useState([]);
  const [teamDetail, setTeamDetail] = useState(null);

  const handleTeamClick = async (row) => {
    const teamIdStr = String(row.teamId);
    try {
      const bootcampId = await getPreferredBootcampIdForTeam(row.teamId);
      if (!bootcampId) {
        setTeam(teamIdStr);
        return;
      }

      writeSelection({
        client: String(clientId),
        region: regionId ? String(regionId) : '',
        team: teamIdStr,
        user: '',
      });

      const params = new URLSearchParams();
      params.set('client', String(clientId));
      if (regionId) params.set('region', String(regionId));
      params.set('team', teamIdStr);
      params.set('portal', isAdminAnalytics ? 'admin' : 'manager');

      navigate({
        pathname: `/bootcamp/${bootcampId}`,
        search: params.toString(),
        state: {
          analyticsBack: {
            clientId: String(clientId),
            regionId: regionId ? String(regionId) : '',
            regionName: selectedRegion?.name || 'Region',
            teamId: teamIdStr,
            teamName: row.teamName || 'Team',
            basePath: analyticsBasePath,
          },
        },
      });
    } catch {
      setError('Could not open team bootcamp');
    }
  };

  useEffect(() => {
    if (hierarchyLoading || !clientId) return;
    let mounted = true;
    const requestedClientId = String(clientId);

    if (!regionId && !teamId && hasClientScope) {
      setAnalyticsByClient({});
      setTopicByClient({});
    }

    const loadClientOverview = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const uid = sessionData?.session?.user?.id;
        if (hasClientScope && !regionId && !teamId) {
          const cacheKey = clientOverviewCacheKey(isAdminAnalytics, uid, requestedClientId);
          const cached = readOverviewCache(cacheKey);
          if (cached) {
            if (!mounted || requestedClientId !== String(clientId)) return;
            setAnalyticsByClient({ [requestedClientId]: cached.analyticsByClient || [] });
            setTopicByClient({ [requestedClientId]: cached.topicByClient || [] });
            setLoading(false);
            return;
          }

          const [analytics, topicActivity] = await Promise.all([
            getRegionActivityByClient([requestedClientId]),
            getTopicActivityByClient([requestedClientId]),
          ]);
          if (!mounted || requestedClientId !== String(clientId)) return;
          const regionRows = analytics.regionsByClient?.[requestedClientId] || [];
          const topicRows = topicActivity?.[requestedClientId] || [];
          setAnalyticsByClient({ [requestedClientId]: regionRows });
          setTopicByClient({ [requestedClientId]: topicRows });
          writeOverviewCache(cacheKey, {
            analyticsByClient: regionRows,
            topicByClient: topicRows,
          });
        } else if (regionId && !teamId) {
          const requestedRegionId = String(regionId);
          const cacheKey = regionOverviewCacheKey(isAdminAnalytics, uid, requestedClientId, requestedRegionId);
          const cached = readOverviewCache(cacheKey);
          if (cached?.regionTeams) {
            if (!mounted || requestedRegionId !== String(regionId) || requestedClientId !== String(clientId)) return;
            setRegionAnalytics(cached.regionTeams);
            setLoading(false);
            return;
          }

          const analytics = await getTeamActivityByRegion(clientId, [regionId]);
          if (!mounted || requestedRegionId !== String(regionId) || requestedClientId !== String(clientId)) return;
          const teamRows = analytics.rowsByRegion[requestedRegionId] || [];
          setRegionAnalytics(teamRows);
          writeOverviewCache(cacheKey, { regionTeams: teamRows });
        } else if (teamId) {
          const requestedTeamId = String(teamId);
          const requestedRegionId = regionId ? String(regionId) : 'none';
          const cacheKey = teamDetailCacheKey(
            isAdminAnalytics,
            uid,
            requestedClientId,
            requestedRegionId,
            requestedTeamId
          );
          const cached = readOverviewCache(cacheKey);
          if (cached?.teamDetail) {
            if (!mounted || requestedTeamId !== String(teamId) || requestedClientId !== String(clientId)) return;
            setTeamDetail(cached.teamDetail);
            setLoading(false);
            return;
          }

          const detail = await getTeamDetail(clientId, regionId, teamId);
          if (!mounted || requestedTeamId !== String(teamId) || requestedClientId !== String(clientId)) return;
          setTeamDetail(detail);
          writeOverviewCache(cacheKey, { teamDetail: detail });
        }
      } catch {
        if (mounted) setError('Failed to load overview');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadClientOverview();
    return () => { mounted = false; };
  }, [clientId, regionId, teamId, userId, hasClientScope, hierarchyLoading, overviewCacheNamespace, isAdminAnalytics]);

  if (hierarchyLoading) {
    return <div className="modern-loading">Loading…</div>;
  }

  if (!clientId) {
    return (
      <AdminAnalyticsLanding
        hierarchy={hierarchy}
        showGlobalKpis={isAdminAnalytics}
      />
    );
  }

  if (loading) {
    return <div className="modern-loading">Loading overview…</div>;
  }

  if (error) {
    return <div className="modern-error">{error}</div>;
  }

  if (isAdminAnalytics && userId && teamDetail) {
    return (
      <div className="modern-list-panel" style={{ padding: 18 }}>
        <p style={{ color: '#475569', marginBottom: 12 }}>{hierarchy.selectedUser?.email}</p>
        {teamDetail.activeBootcamp ? (
          <button
            type="button"
            className="modern-button modern-button-success"
            onClick={() => navigate(`/bootcamp/${teamDetail.activeBootcamp.id}`)}
          >
            View Active Bootcamp →
          </button>
        ) : teamDetail.upcomingBootcamp ? (
          <button
            type="button"
            className="modern-button modern-button-info"
            onClick={() => navigate(`/bootcamp/${teamDetail.upcomingBootcamp.id}`)}
          >
            View Upcoming Bootcamp →
          </button>
        ) : teamDetail.latestCompletedBootcamp ? (
          <button
            type="button"
            className="modern-button modern-button-warning"
            onClick={() => navigate(`/bootcamp/${teamDetail.latestCompletedBootcamp.id}`)}
          >
            View Latest Bootcamp →
          </button>
        ) : (
          <div className="modern-list-empty">No bootcamps for this team.</div>
        )}
      </div>
    );
  }

  if (isAdminAnalytics && teamId && teamDetail) {
    return (
      <div className="modern-list-panel" style={{ padding: 18 }}>
        <div className="modern-flex modern-flex-wrap modern-gap-2 modern-mb-4">
          {(teamDetail.users || []).length === 0 ? (
            <span style={{ color: '#94a3b8' }}>No users in this team.</span>
          ) : (
            (teamDetail.users || []).map((u) => (
              <div key={u.id} className="modern-user-tag" title={u.email || ''}>
                {u.displayName}
              </div>
            ))
          )}
        </div>
        {teamDetail.activeBootcamp ? (
          <button
            type="button"
            className="modern-button modern-button-success"
            onClick={() => navigate(`/bootcamp/${teamDetail.activeBootcamp.id}`)}
          >
            View Active Bootcamp →
          </button>
        ) : teamDetail.upcomingBootcamp ? (
          <button type="button" className="modern-button modern-button-info" onClick={() => navigate(`/bootcamp/${teamDetail.upcomingBootcamp.id}`)}>
            View Upcoming Bootcamp →
          </button>
        ) : teamDetail.latestCompletedBootcamp ? (
          <button type="button" className="modern-button modern-button-warning" onClick={() => navigate(`/bootcamp/${teamDetail.latestCompletedBootcamp.id}`)}>
            View Latest Bootcamp →
          </button>
        ) : (
          <div className="modern-list-empty">No bootcamps for this team.</div>
        )}
      </div>
    );
  }

  if (regionId) {
    return (
      <div>
        <AdminRegionAnalyticsSummary clientId={clientId} regionId={regionId} />
        <AdminRegionActivityLists
          teamRows={regionAnalytics}
          onTeamClick={handleTeamClick}
        />
      </div>
    );
  }

  if (hasClientScope) {
    const regionRows = analyticsByClient[String(clientId)] || [];
    const topicRows = topicByClient[String(clientId)] || [];

    return (
      <div>
        <AdminClientAnalyticsSummary clientId={clientId} />
        <AdminClientActivityLists
          regionRows={regionRows}
          topicRows={topicRows}
          onRegionClick={(row) => hierarchy.setRegion(String(row.regionId))}
        />
      </div>
    );
  }

  return (
    <div className="modern-list-panel">
      <div className="modern-list-empty">Select a region to view team activity.</div>
    </div>
  );
}

export default ManagerOverviewPanel;
